<template lang="pug">
.policy-modal
  modal(:closable="false", @close="close")
    template(#title) {{ title }}

    template(v-if="currentStep === 'policyAcceptance' && isPolicyAcceptance")
      template(
        v-for="(item, index) in requiredActions",
        :key="`policy-${index}`"
      )
        CheckboxInput.mb-1.mr-1(
          v-if="item.type === 'termsAndConditions'",
          v-model="isTerms"
        )
          span
            i18n-t(keypath="termsAccept", for="auth.tos")
              a(:href="termsAndConditionsLink", target="_blank") &nbsp; {{ $t("tos") }} &nbsp;
        CheckboxInput.mb-2.mr-1(
          v-if="item.type === 'privacyPolicy'",
          v-model="isDataPolicy"
        )
          span
            i18n-t(keypath="privacyAccept", for="auth.privacyPolicy")
              a(:href="privacyPolicyLink", target="_blank") &nbsp; {{ $t("privacyPolicy") }} &nbsp;

    template(
      v-else-if="currentStep === 'umapRequirement' && isUmapRequirement"
    )
      h3.mb-2 {{ $t("add_mandatory_user_property") }}
      template(
        v-for="(item, index) in requiredActions",
        :key="`action-${index}`"
      )
        .mb-1(v-if="item.type === 'umapRequirement'")
          label(:for="item.requirement?.tagType") {{ item.tagType?.name }}
          el-select(
            v-if="item.requirement?.multiSelect",
            :id="item.requirement?.tagType",
            v-model="selection[item.tagType?.uuid || '']",
            filterable,
            multiple
          )
            el-option(
              v-for="option in tagValuesList(item.tagType?.uuid || '')",
              :key="option.key",
              :label="option.value",
              :value="option.key"
            )
          el-select(
            v-else,
            :id="item.requirement?.tagType",
            v-model="selection[item.tagType?.uuid || '']",
            filterable
          )
            el-option(
              v-for="option in tagValuesList(item.tagType?.uuid || '')",
              :key="option.key",
              :label="option.value",
              :value="option.key"
            )
      a.txt-center.txt-large(
        v-if="additionalPropertyHelpLink",
        :href="additionalPropertyHelpLink.replace('{lang}', langCode)",
        target="_blank"
      ) {{ $t("help") }}

    .email-verification(
      v-else-if="currentStep === 'emailVerification' && isEmailConfirmation"
    )
      span {{ $t("confirmEmailAddressBody", { email: userInteractionCache.email }) }}
      .flex.flex-row.mt-2
        label.mr-1(:for="'confirmationCode'") {{ $t("confirmationCode") }}
        el-input#confirmationCode(v-model="confirmationCode")

    template(#footer)
      el-button(
        :disabled="!currentStepCompleted",
        :loading="isSubmitting",
        type="primary",
        @click="submitCurrentStep"
      )
        | {{ submitButtonText }}
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { ElNotification } from "element-plus";
import Modal from "@/components/elements/Modal.vue";
import CheckboxInput from "@/components/elements/CheckboxInput.vue";
import { getSetting } from "@/getSettings";
import type { GeneralTypes } from "@inctec/elearning-ui-client";
const i18n = useI18n();

// Extended types for login actions that may not be fully defined in the API

const { locale, t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

// Step management
type Step = "policyAcceptance" | "umapRequirement" | "emailVerification";
const currentStep = ref<Step | null>(null);
const isSubmitting = ref(false);
let pollingInterval: number | null = null;

// Form state
const selection = ref<Record<string, string | string[]>>({});
const isDataPolicy = ref(false);
const isTerms = ref(false);
const confirmationCode = ref<string>("");

const userInteractionCache = computed(() => store.getters.userInteractionCache);

const requiredActions = computed<
  NonNullable<GeneralTypes.LoginActionsRequiredResponse["actionsRequired"]>
>(() => userInteractionCache.value.requiredActions || []);

const additionalPropertyHelpLink = computed(() => {
  return getSetting("additionalPropertyHelpLink");
});

const isPolicyAcceptance = computed(() =>
  requiredActions.value.some(
    (item) =>
      item.type === "privacyPolicy" || item.type === "termsAndConditions",
  ),
);

const isUmapRequirement = computed(() =>
  requiredActions.value.some((item) => item.type === "umapRequirement"),
);

const isEmailConfirmation = computed(() =>
  requiredActions.value.some((item) => item.type === "emailVerification"),
);

const langCode = computed(() => locale.value);

const nextRoute = computed(() => (route.query["next"] as string) || null);

const title = computed(() => {
  switch (currentStep.value) {
    case "policyAcceptance":
      return t("acceptPolicies");
    case "umapRequirement":
      return t("additionalDetails");
    case "emailVerification":
      return t("confirmEmailAddress");
    default:
      return "";
  }
});

const submitButtonText = computed(() => {
  switch (currentStep.value) {
    case "policyAcceptance":
      return t("policyAcceptanceSave");
    case "umapRequirement":
      return t("additionalDetailsSave");
    case "emailVerification":
      return t("confirm");
    default:
      return t("continue");
  }
});

const currentStepCompleted = computed(() => {
  switch (currentStep.value) {
    case "policyAcceptance":
      return requiredActions.value
        .filter(
          (item) =>
            item.type === "termsAndConditions" || item.type === "privacyPolicy",
        )
        .every((item) => {
          if (item.type === "termsAndConditions") return isTerms.value;
          if (item.type === "privacyPolicy") return isDataPolicy.value;
          return false;
        });

    case "umapRequirement":
      return requiredActions.value
        .filter((item) => item.type === "umapRequirement")
        .every((item) => {
          const uuid = item.tagType?.uuid;
          return (
            uuid &&
            selection.value[uuid] !== undefined &&
            selection.value[uuid] !== ""
          );
        });

    case "emailVerification":
      return confirmationCode.value.trim().length > 0;

    default:
      return false;
  }
});

// Determine the next step based on current requirements
function determineNextStep(): Step | null {
  // Priority order: policyAcceptance -> umapRequirement -> emailVerification
  if (isPolicyAcceptance.value) {
    return "policyAcceptance";
  }
  if (isUmapRequirement.value) {
    return "umapRequirement";
  }
  if (isEmailConfirmation.value) {
    return "emailVerification";
  }
  throw new Error("No steps available");
  return null;
}

function tagValuesList(tagTypeId: string) {
  const item = requiredActions.value.find(
    (item) =>
      item.type === "umapRequirement" && item.tagType?.uuid === tagTypeId,
  );

  if (!item || item.type !== "umapRequirement" || !item.tagValues) return [];

  return item.tagValues
    .map((tagValue: { valueText: string; uuid: string }) => ({
      value: tagValue.valueText,
      key: tagValue.uuid,
    }))
    .toSorted((a: { value: string }, b: { value: string }) =>
      a.value.localeCompare(b.value),
    );
}

function buildCurrentStepResponse(): NonNullable<
  GeneralTypes.LoginPost["actionsRequired"]
>[0][] {
  const result: NonNullable<GeneralTypes.LoginPost["actionsRequired"]>[0][] =
    [];

  switch (currentStep.value) {
    case "policyAcceptance":
      if (isTerms.value) {
        result.push({ type: "termsAndConditions" });
      }
      if (isDataPolicy.value) {
        result.push({ type: "privacyPolicy" });
      }
      break;

    case "umapRequirement":
      Object.keys(selection.value).forEach((id) => {
        const value = selection.value[id];
        if (value !== undefined && value !== "") {
          result.push({
            type: "umapRequirement",
            tagType: id,
            tagValue: Array.isArray(value) ? value : [value],
          });
        }
      });
      break;

    case "emailVerification":
      result.push({
        type: "emailVerification",
        code: confirmationCode.value.trim(),
      });
      break;
  }

  return result;
}

async function submitCurrentStep() {
  if (!currentStepCompleted.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      email: userInteractionCache.value.email,
      password: userInteractionCache.value.password,
      actionsRequired: buildCurrentStepResponse() as NonNullable<
        GeneralTypes.LoginActionsRequiredResponse["actionsRequired"]
      >,
    };

    const response = await store.dispatch("login", payload).catch((e) => {
      if (e.response && e.response.status === 451) {
        return e.response.data;
      }
      throw e;
    });

    // Check if there are more actions required
    if (
      response &&
      response.actionsRequired &&
      response.actionsRequired.length > 0
    ) {
      // Check if email verification failed (submitted code but still requires email verification)
      if (
        currentStep.value === "emailVerification" &&
        confirmationCode.value.trim().length > 0 &&
        response.actionsRequired.some(
          (action: { type: string }) => action.type === "emailVerification",
        )
      ) {
        // Email verification code was wrong or expired
        ElNotification({
          type: "error",
          message: t("emailVerificationFailed"),
        });

        // Clear the confirmation code for retry
        confirmationCode.value = "";

        isSubmitting.value = false;
        return;
      }

      // Update the store with new required actions
      store.commit("setUserInteractionRequired", {
        email: userInteractionCache.value.email,
        password: userInteractionCache.value.password,
        requiredActions: response.actionsRequired,
      });

      // Reset form state for next step
      resetFormState();

      // Move to next step
      currentStep.value = determineNextStep();
    } else {
      // No more actions required, login successful
      store.commit("cancelUserInteractionRequired");
      void router.push({ path: nextRoute.value || "/" });
    }
  } catch (error) {
    // Error handled by store/notification system
    console.error("Step submission error:", error);
  } finally {
    isSubmitting.value = false;
  }
}

function resetFormState() {
  // Reset all form fields for the next step
  isTerms.value = false;
  isDataPolicy.value = false;
  selection.value = {};
  confirmationCode.value = "";
}

function close() {
  store.commit("cancelUserInteractionRequired");
  stopPolling();
}

// Polling functionality for email verification
function startPolling() {
  if (pollingInterval) {
    return; // Already polling
  }

  pollingInterval = window.setInterval(
    () => {
      void checkEmailVerification();
    },
    getSetting("pollingInterval") ?? 5000,
  ); // Poll every 5 seconds
}

async function checkEmailVerification() {
  try {
    // Try to login without any actionsRequired to check if email was verified
    const payload = {
      email: userInteractionCache.value.email,
      password: userInteractionCache.value.password,
    };

    const response = await store.dispatch("login", payload).catch((e) => {
      if (e.response && e.response.status === 451) {
        // Still has requirements, return the response data
        return e.response.data;
      }
      // Other errors should stop polling
      throw e;
    });

    // If we got a successful response without actions required, login was successful
    if (
      response &&
      (!response.actionsRequired || response.actionsRequired.length === 0)
    ) {
      stopPolling();
      store.commit("cancelUserInteractionRequired");
      void router.push({ path: nextRoute.value || "/" });
    } else if (
      response &&
      response.actionsRequired &&
      response.actionsRequired.length > 0
    ) {
      // Check if requirements have changed (e.g., email verified but new requirements added)
      const currentRequirements = requiredActions.value;
      const newRequirements = response.actionsRequired;

      // Check if the requirements are different
      const requirementsChanged =
        currentRequirements.length !== newRequirements.length ||
        !currentRequirements.every(
          (req, index) => req.type === newRequirements[index]?.type,
        );

      if (requirementsChanged) {
        // Requirements changed, update the store and move to next step
        stopPolling();
        store.commit("setUserInteractionRequired", {
          email: userInteractionCache.value.email,
          password: userInteractionCache.value.password,
          requiredActions: newRequirements,
        });

        // Reset form state for next step
        resetFormState();

        // Move to next step
        currentStep.value = determineNextStep();
      }
      // If requirements haven't changed, continue polling
    }
  } catch (error) {
    console.error("Polling error:", error);
    // Don't stop polling on error, user might still verify
  }
}

function stopPolling() {
  if (pollingInterval) {
    window.clearInterval(pollingInterval);
    pollingInterval = null;
  }
}
const privacyPolicyLink = computed(() => {
  return getSetting("privacyPolicyLink").replace(
    "$langcode",
    locale.value.toLowerCase(),
  );
});

const termsAndConditionsLink = computed(() => {
  return getSetting("termsAndConditionsLink").replace(
    "$langcode",
    locale.value.toLowerCase(),
  );
});
// Watch for step changes to start/stop polling
watch(currentStep, (newStep) => {
  if (newStep === "emailVerification") {
    startPolling();
  } else {
    stopPolling();
  }
});

// Initialize the first step on mount
onMounted(() => {
  currentStep.value = determineNextStep();
});

// Cleanup on unmount
onUnmounted(() => {
  stopPolling();
});
</script>

<style scoped>
.policy-modal {
  z-index: 100;
  max-width: 700px;
}

.email-verification {
  max-width: 512px;
}
</style>
